/**
 * BRAND RENAME SERVICE
 *
 * Rename a brand across:
 *   1. The `brands/{brandId}` doc's `name` field.
 *   2. Every user item with matching `brandLower` — updates `brand`,
 *      `brandLower`, and recomputes `slug` from the new brand + item's
 *      existing name.
 *
 * The polishCatalog trigger fires on each item write in step 2, which
 * rebuilds catalog docs (new slug creates a new doc; old slug becomes
 * a zero-collector ghost and is cleaned up by the trigger's terminal
 * delete branch).
 *
 * Explicitly OUT of scope for v1:
 *   - polishConfirms migration (rare enough to defer)
 *   - creations migration (rare enough to defer)
 *   - brand-doc merges (needs cross-account coordination — admin-only)
 *
 * Common case: casing fixes ("mooncat" → "Mooncat"). Slugify normalizes
 * case, so slugs don't change — this becomes a simple field rewrite
 * with no downstream slug cascade.
 *
 * Broader renames ("Mooncat" → "Moocat" typo fix): slugs DO change, and
 * the polishCatalog trigger handles the natural rebuild. Confirms and
 * creations may briefly point at the old slug until an admin follow-up.
 */

import {
  collectionGroup,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { polishSlug } from '../utils/polishSlug';

/** Firestore batch cap padded for downstream trigger writes. */
const BATCH_LIMIT = 450;

export interface RenameBrandInput {
  brandId: string;
  /** Lowercase form of the CURRENT brand name — used to query user
   *  items. Passed explicitly rather than derived to make the caller
   *  consciously pick which brand string to normalize on (matters when
   *  the brand doc's `name` diverged from what users historically typed). */
  fromBrandLower: string;
  /** New display casing/spelling of the brand. */
  toBrand: string;
}

export interface RenameBrandSummary {
  itemsScanned: number;
  itemsUpdated: number;
  brandDocUpdated: boolean;
}

export async function renameBrand(
  input: RenameBrandInput
): Promise<RenameBrandSummary> {
  const { brandId } = input;
  const fromBrandLower = input.fromBrandLower.trim().toLowerCase();
  const toBrand = input.toBrand.trim();
  const toBrandLower = toBrand.toLowerCase();

  if (!brandId) throw new Error('brandId is required');
  if (!fromBrandLower) throw new Error('fromBrandLower is required');
  if (!toBrand) throw new Error('toBrand is required');
  if (fromBrandLower === toBrandLower && toBrand === toBrand.trim()) {
    // Casing-only + spelling-only checks handled downstream; nothing to
    // guard here beyond a very obvious no-op.
  }

  // 1) Brand doc first — quick single-doc write. If this fails, we
  // don't touch user items either.
  await updateDoc(doc(db, 'brands', brandId), { name: toBrand });

  // 2) Sweep user items with matching brandLower and rewrite the brand
  // + slug fields. Skip non-user items (brand items, if any legacy
  // are indexed under this brand, are managed via their own UI).
  const snap = await getDocs(
    query(collectionGroup(db, 'items'), where('brandLower', '==', fromBrandLower))
  );

  let itemsScanned = 0;
  let itemsUpdated = 0;
  let batch = writeBatch(db);
  let ops = 0;

  const commitIfFull = async () => {
    if (ops >= BATCH_LIMIT) {
      await batch.commit();
      batch = writeBatch(db);
      ops = 0;
    }
  };

  for (const d of snap.docs) {
    itemsScanned++;
    if (d.ref.parent.parent?.parent?.id !== 'users') continue;
    const data = d.data() as Record<string, unknown>;
    if (data.isDeleted === true) continue;
    const itemName = typeof data.name === 'string' ? data.name.trim() : '';
    const patch: Record<string, unknown> = {
      brand: toBrand,
      brandLower: toBrandLower,
    };
    if (itemName) {
      patch.slug = polishSlug(toBrand, itemName);
    }
    batch.update(d.ref, patch);
    ops++;
    itemsUpdated++;
    await commitIfFull();
  }

  if (ops > 0) await batch.commit();

  return {
    itemsScanned,
    itemsUpdated,
    brandDocUpdated: true,
  };
}
